import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../services/api';
import {
  supabase,
  isSupabaseConfigured,
  signOutSupabase,
  hasPersistedSupabaseSession
} from '../services/supabaseClient';
import {
  readValidCache,
  writeCache,
  clearCache as clearLastVerifiedCache
} from '../services/lastVerifiedUserCache';

/**
 * Opt-in debug logger. Activated by setting
 *   localStorage.setItem('fmb:authDebug', '1')
 * in DevTools, then reloading. Silent in production by default — keeps the
 * shipped bundle's console clean while still allowing field debugging
 * (e.g. for hosted-FE race-condition reports) without a redeploy.
 *
 * NEVER passes tokens or PII beyond what /me already returns.
 */
function authDebugLog(label, payload) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('fmb:authDebug') === '1'
    ) {
      // eslint-disable-next-line no-console
      console.log('[AUTH_RCA]', label, payload || '');
    }
  } catch {
    /* ignore */
  }
}

const AuthContext = createContext(null);

/**
 * Boot timing — staged so we never feel stuck and never give up too early
 * on a cold Render backend:
 *   - When a persisted Supabase session exists, we first hit a lightweight
 *     warmup probe (/api/health) bounded by WARMUP_TIMEOUT_MS. This serialises
 *     the cold-start wait onto a public, DB-free, auth-free endpoint so that
 *     the subsequent /me runs against a (likely) warm BE. Warmup failure is
 *     tolerated — we still attempt /me — because the BE is the source of truth.
 *   - First `/me` attempt races against ME_TIMEOUT_FIRST_MS.
 *   - On timeout we DO NOT purge — we retry once with ME_TIMEOUT_SECOND_MS,
 *     since the most common cause is a cold backend (10–15s wake-up).
 *   - Total worst-case bootstrap is bounded by BOOT_TIMEOUT_MS.
 *
 * Note on BOOT_TIMEOUT_MS = 20s:
 *   In the warm-BE case (and the common cold-but-fast-wake case), warmup
 *   resolves in well under 1 s, leaving 18 s of headroom for the staged /me
 *   retry (6 + 12). On a truly slow cold wake (>20 s), the boot will settle
 *   into a "timeout_recoverable" state (NOT a logout — see resolveProfile
 *   error classification) and the user gets Retry / Sign out / Reload
 *   buttons on the loader. We deliberately kept this at 20 s rather than
 *   raising it: a longer silent wait is more frustrating than an explicit
 *   recovery prompt, and the GitHub Actions keep-awake workflow (see
 *   docs/UPTIME_MONITORING.md) means cold starts should be rare in normal
 *   operation.
 */
const WARMUP_TIMEOUT_MS = 8000;
const ME_TIMEOUT_FIRST_MS = 6000;
const ME_TIMEOUT_SECOND_MS = 12000;
const BOOT_TIMEOUT_MS = 20000;

/**
 * authReason — the categorical outcome of a settled boot or retry. Drives
 * route-guard behavior (redirect to /access-denied, /login, or stay and
 * render the recovery UI). UI never trusts this for authorization.
 *
 * | reason         | session preserved? | next UI                  |
 * |----------------|--------------------|--------------------------|
 * | null           | n/a                | normal (user or /login)  |
 * | NOT_INVITED    | no — purged        | /access-denied           |
 * | INACTIVE       | no — purged        | /access-denied           |
 * | DOMAIN_BLOCKED | no — purged        | /access-denied           |
 * | BOOT_TIMEOUT   | YES — preserved    | recovery loader (Retry…) |
 * | ERROR          | YES — preserved    | recovery loader (Retry…) |
 *
 * "Recoverable" reasons (BOOT_TIMEOUT, ERROR) are shown via full-screen
 * recovery only when there's no cached identity. When a valid cached user
 * is present, the same transient failure surfaces as a non-blocking
 * `authWarning` banner instead.
 */
export const AUTH_RECOVERABLE_REASONS = ['BOOT_TIMEOUT', 'ERROR'];

/**
 * authWarning — non-blocking status for stale-while-revalidate.
 * Only ever set when there IS a cached user; otherwise we use authReason +
 * full-screen recovery instead.
 *
 *   - 'RECONNECTING' : background /me failed transiently (timeout / 5xx /
 *                       network). The cached user keeps using the app
 *                       (read-only-safe paths); a small banner offers Retry.
 */
export const AUTH_WARNINGS = { RECONNECTING: 'RECONNECTING' };

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

function deadline(ms, label) {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error(label || 'AUTH_BOOT_TIMEOUT')), ms)
  );
}

/**
 * Wipe every browser auth artifact we know about. Designed to be safe to call
 * even when the user has never signed in. We DON'T blindly `localStorage.clear()`
 * — that would nuke unrelated app preferences. Instead we target:
 *   - any localStorage key starting with `sb-` (Supabase client persistence)
 *   - the legacy `token` key (defensive — removed from the codebase but a
 *     returning user may still have it cached)
 *   - the lastVerifiedUser cache (must die with the session)
 *   - sessionStorage entirely (no app-level data lives there today)
 *   - Supabase server-side session via signOut
 */
async function purgeBrowserAuthArtifacts() {
  try { await signOutSupabase(); } catch (e) { /* ignore */ }
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') || key === 'token') {
        localStorage.removeItem(key);
      }
    }
    sessionStorage.clear();
  } catch (e) {
    console.warn('[auth] storage cleanup failed', e);
  }
  // The lastVerifiedUser cache is auth-bound; clear it too so the next
  // boot can't show a previous identity's role to whoever logs in next.
  clearLastVerifiedCache();
}

/**
 * Classify a resolveProfile error so the boot pipeline can make exactly
 * one decision per failure mode. The classification determines two things:
 *   - `purge`: whether to wipe Supabase storage + sign out.
 *   - `reason`: which authReason to surface (null = no banner / generic).
 *
 * The cardinal rule: a transient backend hiccup (timeout, 5xx, network
 * blip) must NEVER purge — only authoritative server signals (401 = bad/
 * expired token, 403 with a canonical code = user-level rejection) cause
 * a purge.
 *
 * `transient: true` is the signal the caller needs to decide between
 * full-screen recovery (no cached user) and inline banner (cached user
 * present). The `reason` is still set so the recovery loader has the
 * right copy, but the SWR path will choose to use `authWarning` instead.
 */
function classifyMeError(err) {
  if (err && err.message === 'ME_TIMEOUT') {
    return { purge: false, reason: 'BOOT_TIMEOUT', transient: true };
  }
  const status = err?.response?.status;
  const code = err?.response?.data?.error;
  // BE signals a Supabase JWKS / identity-provider outage as
  // 503 AUTH_INFRA_TRANSIENT. The token may be perfectly valid — only the
  // upstream verifier is temporarily unreachable. Route through the
  // existing transient branch so the cached user stays on screen and the
  // reconnect banner appears, instead of letting a generic 5xx fall
  // through (which is also transient, but this code path keeps the
  // signal explicit and unmistakable in logs / debugging).
  if (status === 503 && code === 'AUTH_INFRA_TRANSIENT') {
    return { purge: false, reason: 'ERROR', transient: true };
  }
  if (status === 401) {
    return { purge: true, reason: null, transient: false };
  }
  if (status === 403 && code) {
    return { purge: true, reason: code, transient: false };
  }
  if (status === 403) {
    return { purge: true, reason: null, transient: false };
  }
  // 5xx, network errors, opaque CORS failures, axios timeouts that didn't
  // come from our own ME_TIMEOUT deadline — KEEP session, recoverable.
  return { purge: false, reason: 'ERROR', transient: true };
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReason, setAuthReason] = useState(null);
  /**
   * authWarning — non-blocking banner state. Only set when we have a
   * cached user and the background /me failed transiently. Never blocks
   * route rendering.
   */
  const [authWarning, setAuthWarning] = useState(null);
  const [isRevalidating, setIsRevalidating] = useState(false);
  // Synchronous best-effort hint computed once at mount — used by route guards
  // to choose loader copy ("Restoring your session…" vs plain "Loading…"). NEVER
  // used as proof of identity; the backend is still the authority on every API.
  const [hasPersistedSession] = useState(() => hasPersistedSupabaseSession());
  const supabaseSessionRef = useRef(null);
  const bootedRef = useRef(false);        // ensure boot only runs once
  /**
   * Monotonically increasing attempt id for every resolveProfile call.
   * The race fix: after observing the hosted app land on the recovery
   * loader even when /me returned 200 in <1s, we proved a sequencing
   * race where an older attempt's timeout/error state-write landed AFTER
   * a newer attempt's success. The rule now: every resolveProfile call
   * increments this counter, captures its own attemptId, and refuses to
   * write user/authReason/authWarning if a newer attempt has superseded
   * it. "Latest attempt wins" — older attempts become silent no-ops.
   *
   * Notes:
   *   - 401/403 from the LATEST attempt still purges normally.
   *   - 401/403 from a STALE attempt is dropped (the newer attempt's
   *     /me will re-discover the same authoritative state if it's real).
   *   - A stale ME_TIMEOUT does NOT trigger the "slow — retrying once"
   *     warning or a second /me — both would be misleading.
   */
  const authAttemptSeqRef = useRef(0);
  // Synchronous mirror of the user state. CRITICAL: we set this in
  // setUserSync BEFORE the React state update flushes, so the background
  // revalidation's error path (which runs in the same microtask chain as
  // the SWR cache render) sees the correct identity. If we relied on a
  // `userRef.current = user` line during render, it would be stale because
  // React doesn't render between scheduling setUser and the next async
  // continuation in the same call stack.
  const userRef = useRef(null);
  const setUserSync = useCallback((next) => {
    userRef.current = next;
    setUser(next);
  }, []);

  /**
   * Ask the backend "who am I?". Single source of truth for role + state.
   *
   * Modes:
   *   - background=false (default): foreground boot or explicit retry.
   *     On any failure, clear user and surface a reason / recovery state.
   *   - background=true: stale-while-revalidate revalidation. A cached
   *     user is already in `user` state and on screen. Transient failures
   *     (BOOT_TIMEOUT / ERROR) MUST NOT clear the user — they only set
   *     a non-blocking authWarning. Authoritative failures (401, 403)
   *     still purge.
   *
   * Sequencing: every invocation gets a unique `attemptId` via
   * `authAttemptSeqRef`. Older attempts whose state writes would land
   * AFTER a newer attempt has incremented the counter are dropped —
   * see the comment on `authAttemptSeqRef`. The `source` parameter is a
   * free-text label ('boot', 'boot-cached', 'retry', 'SIGNED_IN',
   * 'TOKEN_REFRESHED', etc.) used for the opt-in debug log only; it does
   * not affect behavior.
   *
   * Always returns; never throws.
   */
  const resolveProfile = useCallback(async ({ background = false, source = 'unknown' } = {}) => {
    const attemptId = authAttemptSeqRef.current + 1;
    authAttemptSeqRef.current = attemptId;
    const isCurrent = () => authAttemptSeqRef.current === attemptId;

    let cacheHit = false;
    try {
      cacheHit = !!readValidCache(supabaseSessionRef.current);
    } catch {
      /* ignore — debug-only hint */
    }
    authDebugLog('me start', { attemptId, source, background, cacheHit });

    if (background) setIsRevalidating(true);
    try {
      let profile;
      try {
        profile = await Promise.race([
          authAPI.me(),
          deadline(ME_TIMEOUT_FIRST_MS, 'ME_TIMEOUT')
        ]);
      } catch (firstErr) {
        if (firstErr?.message !== 'ME_TIMEOUT') throw firstErr;
        // Stale-attempt guard: a newer resolveProfile has already taken
        // over. Don't log the misleading "slow — retrying" warning, don't
        // fire a wasted second /me, don't write any state. The newer
        // attempt owns the outcome from here.
        if (!isCurrent()) {
          authDebugLog('ignored stale timeout', {
            attemptId,
            current: authAttemptSeqRef.current,
            source
          });
          return null;
        }
        if (process.env.NODE_ENV !== 'test') {
          // eslint-disable-next-line no-console
          console.warn('[auth] /me slow — retrying once before giving up');
        }
        authDebugLog('me timeout, retry', { attemptId, source });
        profile = await Promise.race([
          authAPI.me(),
          deadline(ME_TIMEOUT_SECOND_MS, 'ME_TIMEOUT')
        ]);
      }
      if (!isCurrent()) {
        // /me returned 200 but we've been superseded — drop the result
        // without touching user state. The newer attempt will write the
        // fresh profile (or surface its own outcome).
        authDebugLog('ignored stale success', {
          attemptId,
          current: authAttemptSeqRef.current,
          source
        });
        return null;
      }
      setUserSync(profile);
      setAuthReason(null);
      setAuthWarning(null);
      // Persist for the next boot. Only AFTER /me succeeds — never before.
      writeCache(profile, supabaseSessionRef.current);
      authDebugLog('me success', {
        attemptId,
        source,
        role: profile && profile.role
      });
      return profile;
    } catch (err) {
      const { purge, reason, transient } = classifyMeError(err);
      authDebugLog('me error', {
        attemptId,
        source,
        background,
        purge,
        reason,
        transient,
        status: err && err.response && err.response.status
      });

      // Stale-attempt guard for errors too. An older attempt's 401 / 5xx /
      // timeout MUST NOT clobber state if a newer attempt has started —
      // the newer one will reach its own authoritative answer (and if the
      // session really is bad, that newer attempt's /me will also 401 and
      // do the purge correctly with the current sequence).
      if (!isCurrent()) {
        authDebugLog('ignored stale error', {
          attemptId,
          current: authAttemptSeqRef.current,
          source
        });
        return null;
      }

      if (purge) {
        // Authoritative rejection (401 / 403). Tear everything down,
        // including the cache, so the next boot can't fall back to it.
        await purgeBrowserAuthArtifacts();
        setUserSync(null);
        setAuthReason(reason);
        setAuthWarning(null);
      } else if (transient && background && userRef.current) {
        // Stale-while-revalidate happy path: we have a current user on
        // screen and the background revalidator hit a transient failure.
        // KEEP the user. KEEP the cache (the user is still valid as far
        // as the last successful /me told us). Surface a non-blocking
        // banner so the user knows mutations may not land yet.
        setAuthWarning(AUTH_WARNINGS.RECONNECTING);
      } else {
        // No cached user OR foreground call — fall back to the existing
        // full-screen recovery contract. Session is preserved (no purge).
        setUserSync(null);
        setAuthReason(reason);
        setAuthWarning(null);
      }
      return null;
    } finally {
      // Only the latest attempt may flip isRevalidating off. An older
      // attempt unwinding here while a newer one is mid-flight must not
      // mask the newer attempt's spinner state.
      if (background && isCurrent()) setIsRevalidating(false);
      authDebugLog('me end', {
        attemptId,
        current: authAttemptSeqRef.current,
        source
      });
    }
  }, [setUserSync]);

  /**
   * Run the warmup probe (bounded by WARMUP_TIMEOUT_MS) and then /me.
   * Returns when the boot pipeline has settled — never throws.
   */
  const runWarmupThenResolve = useCallback(async ({ background = false, source = 'unknown' } = {}) => {
    await new Promise((resolve) => {
      let timer;
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        resolve();
      };
      timer = setTimeout(() => {
        if (process.env.NODE_ENV !== 'test') {
          // eslint-disable-next-line no-console
          console.warn('[auth] warmup timed out — proceeding to /me');
        }
        finish();
      }, WARMUP_TIMEOUT_MS);
      try {
        const warmP = authAPI.warmup();
        Promise.resolve(warmP).then(finish, (warmErr) => {
          if (process.env.NODE_ENV !== 'test') {
            // eslint-disable-next-line no-console
            console.warn('[auth] warmup failed — proceeding to /me', warmErr && warmErr.message);
          }
          finish();
        });
      } catch (warmErr) {
        if (process.env.NODE_ENV !== 'test') {
          // eslint-disable-next-line no-console
          console.warn('[auth] warmup threw — proceeding to /me', warmErr && warmErr.message);
        }
        finish();
      }
    });
    await resolveProfile({ background, source });
  }, [resolveProfile]);

  /**
   * Boot — three branches:
   *   1. No Supabase session → settle unauthenticated immediately.
   *   2. Session + valid cached user → render cache, revalidate in background.
   *   3. Session + no/expired/mismatched cache → strict foreground boot.
   */
  useEffect(() => {
    if (!bootedRef.current) {
      bootedRef.current = true;

      const bootInner = async () => {
        // Step 1: hydrate Supabase session.
        if (isSupabaseConfigured && supabase) {
          const { data } = await supabase.auth.getSession();
          supabaseSessionRef.current = data?.session || null;
        }

        // Branch 1: no session — nothing to verify.
        if (!supabaseSessionRef.current) {
          // Defensive: if a previous tab left a cache here without a
          // session (shouldn't happen, but cheap to handle), evict it.
          clearLastVerifiedCache();
          return;
        }

        // Branch 2: cached identity matches the session — render the
        // app immediately and revalidate in the background. The
        // background path tolerates timeouts/5xx without dropping
        // the user, but still respects 401/403 from the backend.
        const cached = readValidCache(supabaseSessionRef.current);
        if (cached && cached.user) {
          setUserSync(cached.user);
          // The async revalidation runs OUTSIDE the BOOT_TIMEOUT race —
          // it's no longer gating the loader. Fire-and-forget; any
          // failure is captured by resolveProfile's own catch and
          // surfaced as authWarning instead of an unhandled rejection.
          runWarmupThenResolve({ background: true, source: 'boot-cached' }).catch(() => {});
          return;
        }

        // Branch 3: session but no valid cache — strict foreground
        // boot. Existing behavior: full-screen recovery on
        // BOOT_TIMEOUT/ERROR, /access-denied on 403, /login on 401.
        await runWarmupThenResolve({ background: false, source: 'boot' });
      };

      (async () => {
        try {
          await Promise.race([bootInner(), deadline(BOOT_TIMEOUT_MS, 'AUTH_BOOT_TIMEOUT')]);
        } catch (err) {
          // The outer BOOT_TIMEOUT only fires if bootInner itself stalls
          // (e.g. supabase.auth.getSession hung). Treat as recoverable —
          // BUT honor the cached-user contract: if we already showed a
          // cached user (Branch 2), do NOT drop them.
          if (err && err.message === 'AUTH_BOOT_TIMEOUT') {
            // eslint-disable-next-line no-console
            console.warn('[auth] boot timeout — recoverable, session preserved');
            if (userRef.current) {
              setAuthWarning(AUTH_WARNINGS.RECONNECTING);
            } else {
              setAuthReason('BOOT_TIMEOUT');
              setUserSync(null);
            }
          } else {
            // eslint-disable-next-line no-console
            console.error('[auth] boot threw — recoverable, session preserved', err);
            if (userRef.current) {
              setAuthWarning(AUTH_WARNINGS.RECONNECTING);
            } else {
              setAuthReason('ERROR');
              setUserSync(null);
            }
          }
        } finally {
          setLoading(false);
        }
      })();
    }

    let sub;
    if (isSupabaseConfigured && supabase) {
      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        supabaseSessionRef.current = session;
        authDebugLog('onAuthStateChange', { event, hasSession: !!session });
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Race fix: when the user is already on screen (live state or
          // valid SWR cache), a TOKEN_REFRESHED — typically fired by
          // Supabase's _recoverAndRefresh on tab focus / visibility
          // change — must NOT run in foreground mode. A foreground
          // revalidation that hits a transient /me failure would clobber
          // the live user with authReason=BOOT_TIMEOUT/ERROR and bounce
          // the app to the recovery loader. In background mode the same
          // failure becomes a non-blocking authWarning instead.
          //
          // Authoritative outcomes (401, 403) still purge regardless of
          // mode — see resolveProfile error classification.
          const hasUser = !!userRef.current;
          let hasValidCache = false;
          try { hasValidCache = !!readValidCache(session); } catch { /* ignore */ }
          const background = hasUser || hasValidCache;
          await resolveProfile({ background, source: event });
        } else if (event === 'SIGNED_OUT') {
          clearLastVerifiedCache();
          setUserSync(null);
        }
      });
      sub = data?.subscription;
    }

    return () => {
      if (sub) sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Auto-recover the reconnect banner.
   *
   * The reconnect banner was previously sticky: once a transient /me
   * failure raised it, it stayed visible until the user clicked Retry /
   * Dismiss or Supabase fired a TOKEN_REFRESHED event. If the BE
   * recovered and the user just kept using the app, every subsequent
   * authenticated API call (admin/users, surveys, designations, …) was
   * returning 200 — clear proof that the BE was healthy and the JWT was
   * accepted — but the banner had no signal to clear itself.
   *
   * Fix: the axios response interceptor in api.js dispatches a
   * `fmb:auth-healthy` CustomEvent on every 2xx response from a
   * non-public endpoint. Here we listen for it and clear authWarning
   * if it's currently RECONNECTING.
   *
   * Why this is safe:
   *   - We do NOT bypass /api/auth/me as authorization — protected
   *     routes are still gated by `requireAuth` server-side.
   *   - We only react when a non-public endpoint returned 2xx, which
   *     means the BE verified the same JWT that /me uses.
   *   - We never re-grant a purged user: the listener short-circuits
   *     if authWarning is not RECONNECTING (e.g. after a 401 purge,
   *     authWarning is null, so a later stray 2xx cannot revive the
   *     session).
   *   - We do NOT touch user / authReason / cache from here — only the
   *     banner state.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => {
      // Snapshot via the setter callback so we never act on a stale
      // closure of authWarning. If it's not RECONNECTING, this is a
      // no-op state update that React will optimise away.
      setAuthWarning((current) => {
        if (current !== AUTH_WARNINGS.RECONNECTING) return current;
        authDebugLog('banner auto-clear via fmb:auth-healthy', {});
        return null;
      });
    };
    window.addEventListener('fmb:auth-healthy', handler);
    return () => window.removeEventListener('fmb:auth-healthy', handler);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured) {
      throw new Error('Google sign-in is not configured.');
    }
    await authAPI.loginWithGoogle();
  }, []);

  /**
   * Logout — must clear ALL auth artifacts atomically, including the
   * lastVerifiedUser cache.
   */
  const logout = useCallback(async () => {
    await purgeBrowserAuthArtifacts();
    setUserSync(null);
    setAuthReason(null);
    setAuthWarning(null);
    queryClient.clear();
  }, [queryClient, setUserSync]);

  /**
   * Re-run the boot pipeline. Called from the recovery loader's Retry
   * button AND from the reconnect banner's Retry action.
   *
   * When called with a cached user already on screen, this re-runs in
   * background mode (so a second transient failure stays a banner, not
   * a full-screen takeover).
   */
  const retryBoot = useCallback(async () => {
    const hadUser = !!userRef.current;
    if (!hadUser) {
      // Foreground retry from the recovery loader.
      setLoading(true);
      setAuthReason(null);
    }
    setAuthWarning(null);
    try {
      if (isSupabaseConfigured && supabase) {
        const { data } = await supabase.auth.getSession();
        supabaseSessionRef.current = data?.session || null;
      }
      if (!supabaseSessionRef.current) {
        // Session vanished between mount and retry.
        clearLastVerifiedCache();
        setUserSync(null);
        return;
      }
      await Promise.race([
        runWarmupThenResolve({ background: hadUser, source: 'retry' }),
        deadline(BOOT_TIMEOUT_MS, 'AUTH_BOOT_TIMEOUT')
      ]);
    } catch (err) {
      if (hadUser) {
        // We had a cached user on screen — don't kick them out for a
        // retry-time hiccup, just re-arm the warning.
        setAuthWarning(AUTH_WARNINGS.RECONNECTING);
      } else if (err && err.message === 'AUTH_BOOT_TIMEOUT') {
        setAuthReason('BOOT_TIMEOUT');
        setUserSync(null);
      } else {
        setAuthReason('ERROR');
        setUserSync(null);
      }
    } finally {
      if (!hadUser) setLoading(false);
    }
  }, [runWarmupThenResolve, setUserSync]);

  const clearAuthReason = useCallback(() => setAuthReason(null), []);
  const dismissAuthWarning = useCallback(() => setAuthWarning(null), []);

  const value = {
    user,
    loading,
    authReason,
    authWarning,
    isRevalidating,
    hasPersistedSession,
    clearAuthReason,
    dismissAuthWarning,
    loginWithGoogle,
    logout,
    isSupabaseConfigured,
    /** Imperative re-fetch of /me only — used after profile-mutating actions. */
    refreshProfile: resolveProfile,
    /** Re-run the full boot pipeline (warmup + /me). Used by recovery loader + banner. */
    retryBoot
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
